import { useState, useCallback, useEffect } from 'react';
import { ArrowDown } from '@phosphor-icons/react';
import { PaginationWrapper } from '../../Utility/PaginationWrapper';
import { PaginationListType } from '../../../Types/PaginationParams';
import SearchFilter from '../../Utility/SearchFilter';
import useSearchFilter from '../../../Hooks/useSearchFilter';
import ConfigSelectorItem from './ConfigSelectorItem';

interface BaseConfig {
    identifier: string;
    title: string;
}

interface ConfigItem {
    identifier: string;
    title: string;
    description: string;
    config?: BaseConfig;
}

interface ConfigSelectorSectionProps {
    config: BaseConfig;
    selectedItems: string[];
    onToggleItem: (itemId: string) => void;
    i18n: any;
    itemsFunction: (
        configIdentifier: string,
        page: number,
        itemsPerPage: number,
        query?: string | null,
    ) => Promise<{ data: { data: ConfigItem[]; total: number } }>;
    getListType: (configIdentifier: string) => PaginationListType;
    setConfigsWithEndpoints?: (count: number) => void;
}

function ConfigSelectorSection({
    config,
    selectedItems,
    onToggleItem,
    i18n,
    itemsFunction,
    getListType,
}: ConfigSelectorSectionProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [totalItems, setTotalItems] = useState<number>(0);
    const { query, localQuery, searchActions } = useSearchFilter({ initialQuery: null });

    const toggleExpansion = () => {
        setIsExpanded(!isExpanded);
    };

    const wrappedItemsFunction = useCallback(
        async (
            page: number,
            itemsPerPage: number,
            optionalParams?: Record<string, string>,
            queryParam?: string | null,
        ) => {
            return await itemsFunction(config.identifier, page, itemsPerPage, queryParam);
        },
        [itemsFunction, config.identifier],
    );

    useEffect(() => {
        const initLoad = async () => {
            const response = await wrappedItemsFunction(1, 10, {}, null);
            if (response.data.total !== undefined) {
                setTotalItems(response.data.total);
            }
        };
        initLoad();
    }, [wrappedItemsFunction]);

    if (totalItems === 0) {
        return null;
    }

    return (
        <div>
            <button onClick={toggleExpansion} className="w-full flex items-center py-4 text-left">
                <span className="mr-3 text-gray-500 flex justify-center">
                    <button
                        className="rounded-full h-[25px] md:h-[30px] w-[25px] md:w-[30px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover border-none flex items-center justify-center"
                        title={i18n.translate({
                            id: 'selector.showHidePrompts',
                            sourceName: 'PromptFunction',
                        })}
                    >
                        <ArrowDown
                            size={20}
                            style={{
                                transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease',
                            }}
                        />
                    </button>
                </span>
                <span className="text-layout-textcolor flex-1">
                    <span className="font-bold">{config.title}</span>
                    <span className="text-gray-500 text-sm pl-2">
                        ({selectedItems.length}/{totalItems})
                    </span>
                </span>
            </button>

            <div className={`${isExpanded ? 'h-fit ' : 'h-0'} overflow-hidden`}>
                <div className="space-y-2">
                    <div className="pt-4 space-y-3">
                        <SearchFilter
                            value={localQuery}
                            onChange={searchActions.setLocalQuery}
                            onApply={searchActions.applyQuery}
                            onClear={searchActions.clearQuery}
                            className="mb-4 max-w-full"
                        />
                    </div>
                    <div className="pb-4">
                        <PaginationWrapper
                            listType={getListType(config.identifier)}
                            itemsFunction={wrappedItemsFunction}
                            itemsPerPage={10}
                            query={query || null}
                            renderItem={(item: ConfigItem) => (
                                <ConfigSelectorItem
                                    key={item.identifier}
                                    item={item}
                                    isSelected={selectedItems?.includes(item.identifier) || false}
                                    onToggle={onToggleItem}
                                />
                            )}
                            className="space-y-3"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfigSelectorSection;
